import IStorageProvider from '../storage/istorageProvider';

/** First retry after a failed save, doubling from here. */
const retryBaseMs = 60 * 1000;

/** Doubling stops here. */
const retryMaxMs = 10 * 60 * 1000;

/**
 * What a deliberate save achieved.
 *
 * 'failed' has already been reported through onError by the time it comes
 * back; 'nothing' means there was no unsaved change to write.
 */
export type SaveOutcome = 'saved' | 'failed' | 'nothing';

export const fileQueueCreator = function (
  fileName: string,
  setActive: React.MutableRefObject<(task: string) => void>,
  disableActive: React.MutableRefObject<(task: string) => void>,
  debounceTimer = 300,
  /**
   * Told about each failed save, with how long until the retry. The queue
   * has no snackbar of its own and should not grow one; the owner has.
   */
  onError?: (err: unknown, retryInMs: number) => void,
) {
  let interval: NodeJS.Timeout | undefined;

  const state = {
    /**
     * The save currently executing, if any. Resolves true for written, false
     * for failed-and-requeued. Replaces a bare isActive flag so that a
     * deliberate save arriving mid-write can wait for the write rather than
     * finding the queue "busy" and doing nothing - which, with the button
     * then saying "Saved!", is what it used to do.
     */
    inFlight: undefined as Promise<boolean> | undefined,
    /** Next in line */
    queuedReq: undefined as (() => Promise<boolean>) | undefined,
  };

  /** Failed saves since the last one that went through. */
  let failures = 0;

  /**
   * What the next dequeue waits. The ordinary debounce, or a retry backoff
   * once a save has failed - set by the save's own outcome, so finishReq does
   * not have to know which it is dealing with.
   */
  let nextDelay = debounceTimer;

  function finishReq() {
    if (state.queuedReq) {
      debounce(nextDelay);
    }

    disableActive.current(fileName);
  }

  async function dequeue(): Promise<SaveOutcome> {
    // Somebody is already saving. What they are writing is either this data
    // or something it has since superseded - and in the second case the
    // superseding request is sitting in queuedReq for us to run next.
    while (state.inFlight) {
      await state.inFlight;
    }

    if (!state.queuedReq) {
      return 'nothing';
    }

    setActive.current(fileName);

    // If we would execute a resolved promise before cleaning up
    // we might get an infinite self invoking loop
    const toExec = state.queuedReq;
    state.queuedReq = undefined;
    state.inFlight = toExec();

    try {
      return (await state.inFlight) ? 'saved' : 'failed';
    } finally {
      state.inFlight = undefined;
    }
  }

  function debounce(delay = debounceTimer) {
    if (interval) {
      clearTimeout(interval);
    }

    interval = setTimeout(function () {
      dequeue();
      interval = undefined;
    }, delay);
  }

  const q = {
    queue: function (data: any, provider: IStorageProvider) {
      /**
       * One save of this data, however many attempts it takes.
       *
       * A failed save used to leave the queue empty and the rejection uncaught:
       * the edit survived in React state, but nothing would try to write it
       * again until the next edit came along, and closing the app in between
       * lost it without a word - the beforeunload guard below only asks about
       * what is queued. So a failure puts the request back, unless a newer
       * version of the file has been queued meanwhile, in which case that one
       * supersedes it. The next attempt waits a while, longer each time, so a
       * provider that is down is not hammered - but never for ever.
       */
      const attempt = (): Promise<boolean> =>
        provider
          .saveAppData(fileName, data)
          .then(
            () => {
              failures = 0;
              nextDelay = debounceTimer;
              return true;
            },
            (err) => {
              failures += 1;
              nextDelay = Math.min(retryBaseMs * Math.pow(2, failures - 1), retryMaxMs);

              if (!state.queuedReq) {
                state.queuedReq = attempt;
              }

              if (onError) {
                onError(err, nextDelay);
              }

              return false;
            },
          )
          .finally(finishReq);

      state.queuedReq = attempt;

      // A fresh edit waits the ordinary debounce, whatever the last attempt
      // did: the user is here and working, and that is when saving promptly
      // matters most.
      nextDelay = debounceTimer;
      debounce();
    },
    /** Write what is queued now, waiting first for any write in progress. */
    executeSingle(): Promise<SaveOutcome> {
      return dequeue();
    },
    get queuedReq() {
      return state.queuedReq;
    },
    get fileName() {
      return fileName;
    },
  };

  /**
   * Here we only handle a paused queue.
   * Context registers a callback to run if there are runningTasks
   */
  window.addEventListener('beforeunload', (ev: BeforeUnloadEvent) => {
    if (state.queuedReq) {
      const prompt = `${fileName} currently has unsaved changes, would you like to save?`;

      // Fire off the write either way. If the user goes ahead and leaves, it
      // evaporates with the page and nothing is lost that was not already
      // lost; if they stay, it lands. This used to chain a
      // window.location.reload() onto the write, which could only ever run
      // in the second case - the user had just said "stay" and was reloaded
      // regardless, as soon as the save finished.
      q.executeSingle();
      ev.preventDefault();

      ev.returnValue = prompt;
      return prompt;
    }
  });

  return q;
};
